import { Type, type Static } from "@sinclair/typebox";
import type { JobDetail, LifelineDetail } from "./operations.js";
const text = Type.String({ maxLength: 4000 });
const date = Type.Union([Type.String({ format: "date" }), Type.Null()]);
const uuid = Type.String({ format: "uuid" });
const version = Type.Integer({ minimum: 1 });
export const LifelineProfileSchema = Type.Object(
  {
    administratorContact: text,
    contactPhone: text,
    contactEmail: Type.Union([
      Type.Literal(""),
      Type.String({ format: "email", maxLength: 254 }),
    ]),
    roofType: text,
    roofAreaSqm: Type.Number({ minimum: 0, maximum: 10000000 }),
    accessCount: Type.Integer({ minimum: 0, maximum: 100000 }),
    hasPhotovoltaic: Type.Boolean(),
    fascicleStatus: Type.String({ minLength: 1, maxLength: 80 }),
    generalNotes: text,
    installationDate: date,
    devices: text,
    maxOperators: Type.Integer({ minimum: 0, maximum: 100000 }),
    manufacturer: text,
    safetyStatus: Type.Union(
      [
        "Non censito",
        "Non utilizzabile",
        "Non utilizzabile per documentazione assente",
        "Non utilizzabile per anomalie",
        "Utilizzabile",
        "Utilizzabile con prescrizioni",
        "Fuori servizio",
        "Da verifica specialistica",
      ].map((v) => Type.Literal(v)),
    ),
    safetyReason: text,
    responsibleEmployeeId: Type.Union([uuid, Type.Null()]),
    scheduledControlOn: date,
    scheduledDurationDays: Type.Integer({ minimum: 1, maximum: 366 }),
    lastControlOn: date,
    documentVerifiedOn: Type.Optional(date),
    controlIntervalMonths: Type.Optional(
      Type.Union([Type.Literal(12), Type.Literal(24)]),
    ),
    subscriptionLastInvoiceOn: date,
    maintenanceContractAmount: Type.Optional(
      Type.Union([
        Type.Number({ minimum: 0, maximum: 999999999.99 }),
        Type.Null(),
      ]),
    ),
  },
  { additionalProperties: false },
);
export const LifelineChecklistSchema = Type.Object(
  {
    key: Type.String({
      minLength: 1,
      maxLength: 120,
      pattern: "^[a-zA-Z0-9_-]+$",
    }),
    title: Type.String({ maxLength: 240 }),
    status: Type.Union(
      ["missing", "valid", "outdated", "not-applicable"].map((v) =>
        Type.Literal(v),
      ),
    ),
    datedOn: date,
    expiresOn: date,
    reference: text,
    notes: text,
  },
  { additionalProperties: false },
);
export const LifelineSheetSaveSchema = Type.Object(
  {
    operationId: uuid,
    version,
    profile: LifelineProfileSchema,
    checklist: Type.Array(LifelineChecklistSchema, {
      minItems: 6,
      maxItems: 100,
    }),
  },
  { additionalProperties: false },
);
export const LifelineRecordSaveSchema = Type.Object(
  {
    operationId: uuid,
    version,
    entryId: Type.Optional(uuid),
    kind: Type.Union(
      ["deadline", "inspection", "anomaly"].map((v) => Type.Literal(v)),
    ),
    date: Type.String({ format: "date" }),
    type: Type.String({ minLength: 1, maxLength: 100 }),
    status: Type.String({ minLength: 1, maxLength: 80 }),
    description: text,
    responsible: text,
    notes: text,
    reference: text,
    severity: Type.Optional(
      Type.Union(
        ["low", "medium", "high", "critical"].map((v) => Type.Literal(v)),
      ),
    ),
    outcome: Type.Optional(
      Type.Union(
        ["passed", "conditional", "failed", "pending"].map((v) =>
          Type.Literal(v),
        ),
      ),
    ),
    countsAsControl: Type.Optional(Type.Boolean()),
    workDone: Type.Optional(text),
    excludedMaterials: Type.Optional(text),
    photos: Type.Optional(text),
    reportGenerated: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);
export const LifelineRecordCancelSchema = Type.Object(
  {
    operationId: uuid,
    version,
    kind: Type.Union(
      ["deadline", "inspection", "anomaly"].map((v) => Type.Literal(v)),
    ),
    entryId: uuid,
  },
  { additionalProperties: false },
);
export const LifelineOperationSchema = Type.Object(
  { operationId: uuid, version },
  { additionalProperties: false },
);
export type LifelineProfile = Static<typeof LifelineProfileSchema>;
export type LifelineChecklistEntry = Static<typeof LifelineChecklistSchema>;
export type LifelineSheetSave = Static<typeof LifelineSheetSaveSchema>;
export type LifelineRecordSave = Static<typeof LifelineRecordSaveSchema>;
export type LifelineRecordCancel = Static<typeof LifelineRecordCancelSchema>;
export interface LifelineRecord {
  id: string;
  kind: "deadline" | "inspection" | "anomaly";
  date: string;
  type: string;
  status: string;
  description: string;
  responsible: string;
  notes: string;
  reference: string;
  severity?: "low" | "medium" | "high" | "critical";
  outcome?: "passed" | "conditional" | "failed" | "pending";
  countsAsControl?: boolean;
  workDone?: string;
  excludedMaterials?: string;
  photos?: string;
  reportGenerated?: boolean;
  cancelled: boolean;
}
export interface LifelineWorkspace {
  detail: LifelineDetail;
  profile: LifelineProfile;
  checklist: LifelineChecklistEntry[];
  records: LifelineRecord[];
  job: JobDetail | null;
  documentStatus: string;
  lastControlOn: string | null;
  feasibleOn: string | null;
  dueOn: string | null;
  subscriptionExpiresOn: string | null;
  suggestedAnnualFee: number;
  legacyAvailable: boolean;
}
export interface LifelineRegistrySummary {
  controls: number;
  ficRevenue: string;
  costs: string;
  profit: string;
  unconfirmedInvoices: number;
}
